import { type FunctionComponent } from 'react';

import { Image } from '@/core/Image/Image';
import { attachQueryParams } from '@/domains/digitalAdvice/utils/attachQueryParams';

const SIZES = {
  small: {
    hdpi: { width: 656, height: 400 },
    ldpi: { width: 328, height: 200 },
  },
  medium: {
    hdpi: { width: 1104, height: 400 },
    ldpi: { width: 552, height: 200 },
  },
  large: {
    hdpi: { width: 720, height: 720 },
    ldpi: { width: 360, height: 360 },
  },
};

interface Props {
  className?: string;
  image: { alt: string; url: string };
  lazy?: boolean;
}

export const computeSrcSet = (
  imageUrl: string,
  size: 'small' | 'medium' | 'large',
  isWebp: boolean,
) => {
  const { hdpi, ldpi } = SIZES[size];
  const srcSet1x = attachQueryParams({
    urlString: imageUrl,
    params: {
      w: ldpi.width,
      h: ldpi.height,
      fit: 'cover',
      ...(isWebp && { format: 'webp' }),
    },
    densityDescriptor: '1x',
  });
  const srcSet2x = attachQueryParams({
    urlString: imageUrl,
    params: {
      w: hdpi.width,
      h: hdpi.height,
      fit: 'cover',
      ...(isWebp && { format: 'webp' }),
    },
    densityDescriptor: '2x',
  });

  return `${srcSet1x}, ${srcSet2x}`;
};

export const GuideCardImage: FunctionComponent<Props> = ({
  className,
  image,
  lazy = true,
}) => {
  const smallHight = SIZES.small.ldpi.height;
  const smallWidth = SIZES.small.ldpi.width;

  const src = attachQueryParams({
    urlString: image.url,
    params: { w: smallWidth, h: smallHight, fit: 'cover' },
  });

  return (
    <Image
      alt={image.alt}
      className={className}
      data-testid="mobileCard"
      height={smallHight}
      loading={lazy ? 'lazy' : 'eager'}
      src={src}
      srcSet={computeSrcSet(image.url, 'small', false)}
      width={smallWidth}
    >
      <source
        data-testid="desktopCard"
        media="(min-width: 1024px)"
        srcSet={computeSrcSet(image.url, 'large', true)}
        type="image/webp"
      />
      <source
        data-testid="desktopCard"
        media="(min-width: 1024px)"
        srcSet={computeSrcSet(image.url, 'large', false)}
      />
      <source
        data-testid="tabletCard"
        media="(min-width: 600px)"
        srcSet={computeSrcSet(image.url, 'medium', true)}
        type="image/webp"
      />
      <source
        data-testid="tabletCard"
        media="(min-width: 600px)"
        srcSet={computeSrcSet(image.url, 'medium', false)}
      />
      <source
        data-testid="mobileCard"
        srcSet={computeSrcSet(image.url, 'small', true)}
        type="image/webp"
      />
    </Image>
  );
};
